import type { TagDto } from '@_/api-contracts/common.ts';
import { createFileRoute, notFound, useRouter } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { getReactQueryOptions, isApiError } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { SeoTags } from '#src/components/SeoTags/index.ts';
import { ArticlesPage } from '#src/pages/ArticlesPage/index.ts';

export const Route = createFileRoute('/{-$locale}/articles')({
  component: RouteComponent,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, params, deps }) => {
    const { queryClient } = context;
    const { locale } = params;

    let tag: TagDto | undefined;

    if (deps.search.tag) {
      try {
        const resp = await queryClient.ensureQueryData(
          getReactQueryOptions(
            publicApiQueryClient.tags.get,
            { params: { idOrSlug: deps.search.tag } },
            { context: { locale } }
          )
        );

        tag = resp.data;
      } catch (error) {
        if (!isApiError(error) || error.status !== 404) {
          throw error;
        }
      }
    }

    const searchStr = deps.search.search;

    if (!tag && !searchStr) {
      throw notFound();
    }

    return {
      searchStr,
      tag
    };
  },
  validateSearch: z.object({
    search: z.string().trim().nonempty().optional().catch(undefined),
    tag: z.string().trim().nonempty().optional().catch(undefined)
  })
});

function RouteComponent() {
  const { searchStr, tag } = Route.useLoaderData();
  const intl = useIntl();
  const router = useRouter();

  let seoTags: React.ReactNode;
  if (tag) {
    const canonicalPath = router.buildLocation({
      from: '/{-$locale}',
      to: '/{-$locale}/articles',
      search: { tag: tag.slug }
    });

    seoTags = (
      <SeoTags
        canonicalPath={canonicalPath.href}
        title={intl.formatMessage(
          {
            id: 'head.articles-by-topic-page',
            defaultMessage: '{topic} Articles'
          },
          {
            topic: tag.name
          }
        )}
      />
    );
  }

  return (
    <>
      {seoTags}
      <ArticlesPage searchStr={searchStr} tag={tag} />
    </>
  );
}
